import type { ErrorEvent } from "@sentry/browser";
import {
  isChunkLoadErrorMessage,
  isChunkLoadReloadScheduled,
} from "../router/chunk-load-error.handler";

// beforeSend sees raw minified frames (source maps are applied by Sentry after
// ingestion), so nothing pdf.js-specific survives to match on. The app never
// aborts a signal itself, which makes the default reason the fingerprint.
const DEFAULT_ABORT_REASON = "signal is aborted without reason";
const UNHANDLED_REJECTION = "onunhandledrejection";

// pdf.js (via ng2-pdf-viewer) aborts its range fetch whenever the preview is
// torn down mid-load; the rejection escapes unhandled but nothing breaks for
// the user.
export function isPdfJsAbort(event: ErrorEvent): boolean {
  return (event.exception?.values ?? []).some(
    (exception) =>
      exception.type === "AbortError" &&
      exception.value === DEFAULT_ABORT_REASON &&
      (exception.mechanism?.type?.endsWith(UNHANDLED_REJECTION) ?? false),
  );
}

const MAPS_MODULE_LOAD_FAILURE = "Loader.provide not called by module ";

// The Google Maps bootstrap fetched a module script (`places`, `maps`, …) that
// never registered — network drop, content blocker or weekly-version skew.
// Google caches the failure for the page, so every later importLibrary call
// rejects with it until a reload; our callers already catch and log it, but the
// loader's own promise rejects as well. Nothing in our code can recover it.
export function isGoogleMapsModuleLoadFailure(event: ErrorEvent): boolean {
  return (event.exception?.values ?? []).some(
    (exception) =>
      exception.type === "Error" &&
      (exception.value?.startsWith(MAPS_MODULE_LOAD_FAILURE) ?? false),
  );
}

// A tab opened before a deploy fails to load a removed lazy chunk and the
// router reloads it onto the current build; the rejected navigation is still
// reported before the page unloads. A failure the reload guard let through is
// kept, as the user is stuck on it.
export function isRecoveredChunkLoadError(
  event: ErrorEvent,
  view: object = window,
): boolean {
  return (
    isChunkLoadReloadScheduled(view) &&
    (event.exception?.values ?? []).some((exception) =>
      isChunkLoadErrorMessage(exception.value),
    )
  );
}
